import React, { useState } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import {
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
  FaTimes,
  FaSearchPlus,
  FaSearchMinus,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaPrint,
} from 'react-icons/fa';
import '../utils/pdfWorkerConfig'; // Configuración automática del worker
import './PDFComponents.css';

interface PDFReaderProps {
  base64Data: string;
  fileName: string;
  pdfBlob?: Blob; // Blob opcional para descarga
  onClose: () => void;
  onDownload?: () => void; // Función opcional para descarga personalizada
}

const PDFReader: React.FC<PDFReaderProps> = ({ base64Data, fileName, pdfBlob, onClose, onDownload }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Preparar datos para react-pdf 8.0.2
  const pdfData = `data:application/pdf;base64,${base64Data}`;

  // Función para descargar el PDF
  const downloadPDF = () => {
    // Validaciones previas antes del try-catch
    if (!base64Data && !pdfBlob) {
      console.error('❌ No hay datos disponibles');
      alert('No hay datos disponibles para descargar');
      return;
    }

    if (!fileName || fileName.trim() === '') {
      console.error('❌ Nombre de archivo no válido');
      alert('Nombre de archivo no válido');
      return;
    }

    try {
      let downloadBlob: Blob;

      if (pdfBlob && pdfBlob instanceof Blob) {
        // Usar el blob proporcionado si es válido
        downloadBlob = pdfBlob;
      } else {
        // Validar base64 antes de convertir
        if (!base64Data || base64Data.trim() === '') {
          throw new Error('Datos base64 no válidos para la descarga');
        }

        // Limpiar el base64 de posibles prefijos
        const cleanBase64 = base64Data.replace(/^data:application\/pdf;base64,/, '');

        // Convertir base64 a blob
        const byteCharacters = atob(cleanBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        downloadBlob = new Blob([byteArray], { type: 'application/pdf' });
      }

      // Validar que el blob se creó correctamente
      if (!downloadBlob || downloadBlob.size === 0) {
        throw new Error('El archivo PDF generado está vacío o es inválido');
      }

      // Crear URL temporal y descargar
      const url = URL.createObjectURL(downloadBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName.endsWith('.pdf') ? fileName : `${fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      // Mostrar mensaje específico según el tipo de error
      let errorMessage = 'Error al descargar el archivo PDF';
      if (error instanceof Error) {
        errorMessage = error.message;
      }

      alert(`Error en descarga: ${errorMessage}`);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('Error cargando PDF:', error);
    setError(`Error cargando PDF: ${error.message}`);
    setLoading(false);
  };

  // Funciones de control simples
  const nextPage = () => pageNumber < numPages && setPageNumber(pageNumber + 1);
  const prevPage = () => pageNumber > 1 && setPageNumber(pageNumber - 1);
  const zoomIn = () => setScale((prev) => Math.min(prev + 0.25, 3.0));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.25, 0.5));
  const goFirst = () => numPages > 0 && setPageNumber(1);
  const goLast = () => numPages > 0 && setPageNumber(numPages);

  const printPDF = () => {
    try {
      let blob: Blob | null = null;
      if (pdfBlob) blob = pdfBlob;
      else if (base64Data) {
        const clean = base64Data.replace(/^data:application\/pdf;base64,/, '');
        const bytes = atob(clean);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        blob = new Blob([arr], { type: 'application/pdf' });
      }
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const win = window.open(url);
      if (win) {
        win.onload = () => win.print();
      } else {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.src = url;
        document.body.appendChild(iframe);
        iframe.onload = () => {
          iframe.contentWindow?.print();
          setTimeout(() => {
            document.body.removeChild(iframe);
            URL.revokeObjectURL(url);
          }, 1000);
        };
      }
    } catch (e) {
      console.error('Error al imprimir', e);
    }
  };

  if (error) {
    return (
      <div className="pdf-error">
        <h3>Error al cargar el PDF</h3>
        <p>{error}</p>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onClose();
          }}
        >
          Cerrar
        </button>
      </div>
    );
  }

  return (
    <div className="pdf-reader-simple">
      <div className="pdf-controls">
        {/* Primer div: Navegación de páginas (33%) */}
        <div className="pdf-control-section">
          <button
            type="button"
            disabled={pageNumber === 1 || numPages === 0}
            onClick={(e) => {
              e.preventDefault();
              goFirst();
            }}
            title="Primera"
          >
            {FaAngleDoubleLeft({})}
          </button>
          <button
            type="button"
            disabled={pageNumber === 1 || numPages === 0}
            onClick={(e) => {
              e.preventDefault();
              prevPage();
            }}
            title="Anterior"
          >
            {FaChevronLeft({})}
          </button>
          <span>
            Página {numPages ? pageNumber : 0} de {numPages}
          </span>
          <button
            type="button"
            disabled={pageNumber === numPages || numPages === 0}
            onClick={(e) => {
              e.preventDefault();
              nextPage();
            }}
            title="Siguiente"
          >
            {FaChevronRight({})}
          </button>
          <button
            type="button"
            disabled={pageNumber === numPages || numPages === 0}
            onClick={(e) => {
              e.preventDefault();
              goLast();
            }}
            title="Última"
          >
            {FaAngleDoubleRight({})}
          </button>
        </div>

        {/* Segundo div: Controles de zoom (33%) */}
        <div className="pdf-control-section zoom-controls">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              zoomOut();
            }}
            aria-label="Alejar"
            title="Alejar"
          >
            {FaSearchMinus({})}
          </button>
          <span>{Math.round(scale * 100)}%</span>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              zoomIn();
            }}
            aria-label="Acercar"
            title="Acercar"
          >
            {FaSearchPlus({})}
          </button>
        </div>

        {/* Tercer div: Acciones descargar y cerrar (33%) */}
        <div className="pdf-control-section gp10">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              printPDF();
            }}
            aria-label="Imprimir"
            title="Imprimir"
          >
            {FaPrint({})}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              if (onDownload) {
                onDownload();
              } else {
                downloadPDF();
              }
            }}
            aria-label="Descargar PDF"
            title="Descargar"
          >
            {FaDownload({})}
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onClose();
            }}
            aria-label="Cerrar"
            title="Cerrar"
          >
            {FaTimes({})}
          </button>
        </div>
      </div>

      <div className="pdf-viewer-simple">
        {loading && <div className="loading">Cargando PDF...</div>}

        <Document
          file={pdfData}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={<div className="loading">Cargando documento...</div>}
          error={<div className="error">Error al cargar el documento</div>}
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            loading={<div className="loading">Cargando página...</div>}
            error={<div className="error">Error al cargar la página</div>}
          />
        </Document>
      </div>

      {/* Botones de acción debajo del visor */}
      <div className="pdf-actions">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            onClose();
          }}
          className="btn-secondary-custome"
          aria-label="Regresar"
        >
          Regresar
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            if (onDownload) {
              onDownload();
            } else {
              downloadPDF();
            }
          }}
          className="btn-primary-custome"
          aria-label="Descargar PDF"
        >
          Descargar
        </button>
      </div>
    </div>
  );
};

export default PDFReader;
